import { useEffect, useRef, useState } from 'react';
import type { BillingCharge } from '@kawai/shared';
import { fetchRecentBillingCharges } from '../repositories/billingCharges.js';

export type BillingChargesStatus = 'idle' | 'loading' | 'ready' | 'error';

export interface UseBillingChargesResult {
  status: BillingChargesStatus;
  charges: BillingCharge[];
}

/**
 * S-90: 自テナントの直近請求履歴（billing_charges）を取得する hook。
 * ホームのお支払いサマリー用（tenant-portal は React Query 非依存のため
 * `useTenantBrand` と同じ useEffect パターンで実装）。
 *
 * - `tenantId === null`（表示権限なし / saas-admin 純運営者）→ fetch しない（idle）
 * - 取得失敗時もアプリは止めず空配列にフォールバック（status='error'）
 * - test 時は `fetcher` を差し替え可能
 */
export function useBillingCharges(
  tenantId: string | null,
  fetcher: (id: string) => Promise<BillingCharge[]> = fetchRecentBillingCharges,
): UseBillingChargesResult {
  // fetcher を初回値に固定する（useTenantBrand と同じ無限再 fetch 防止 / S-04 PR #88）。
  const fetcherRef = useRef(fetcher);

  const [state, setState] = useState<UseBillingChargesResult>({
    status: tenantId ? 'loading' : 'idle',
    charges: [],
  });

  useEffect(() => {
    if (!tenantId) {
      setState({ status: 'idle', charges: [] });
      return;
    }
    let cancelled = false;
    setState((prev) => ({ status: 'loading', charges: prev.charges }));
    fetcherRef
      .current(tenantId)
      .then((charges) => {
        if (cancelled) return;
        setState({ status: 'ready', charges });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        // 致命ではないので空リストにフォールバック（ホームは他カードを表示し続ける）
        // eslint-disable-next-line no-console
        console.error('[billingCharges] fetch failed:', err);
        setState({ status: 'error', charges: [] });
      });
    return () => {
      cancelled = true;
    };
  }, [tenantId]);

  return state;
}
